import { ReactNode, useEffect, useState } from 'react';
import { Control, FieldValues, Path, useFormContext } from 'react-hook-form';

import { useImageUpload } from '@/hooks/use-image-upload';
import { formatFileSize } from '@/utils/file';

import { Button } from '../ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';

interface AvatarUploadInputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label?: ReactNode;
  description?: ReactNode;
  accept?: string;
  defaultImage?: string;
  maxFileSize: number;
}

function Avatar({
  defaultImage,
  onFileChange,
  description,
  maxFileSize,
}: {
  defaultImage?: string;
  onFileChange: (file: File | null) => void;
  description?: ReactNode;
  maxFileSize: number;
}) {
  const { setError, clearErrors } = useFormContext();
  const {
    previewUrl,
    fileInputRef,
    handleThumbnailClick,
    handleFileChange,
    handleRemove,
  } = useImageUpload({
    onUpload: () => {
      const file = fileInputRef.current?.files?.[0] || null;
      if (file && file.size > maxFileSize) {
        setError('logo', {
          type: 'manual',
          message: `File size must be less than ${formatFileSize(maxFileSize)}`,
        });
        handleRemove();
        return;
      }
      clearErrors('logo'); // Clear error if file is valid
      onFileChange(file);
    },
  });

  const [isDeleted, setIsDeleted] = useState(false);

  const currentImage = !isDeleted ? previewUrl || defaultImage : null;

  useEffect(() => {
    if (previewUrl || defaultImage) {
      setIsDeleted(false);
    }
  }, [previewUrl, defaultImage]);

  const handleDelete = () => {
    handleRemove();
    setIsDeleted(true);
    clearErrors('logo');
    onFileChange(null);
  };

  const canDelete = !!previewUrl || !!defaultImage;

  return (
    <div className="flex items-center gap-4">
      <div className="shadow-xs relative flex size-20 items-center justify-center overflow-hidden rounded-full border-4 border-background bg-muted shadow-black/10">
        {currentImage && !currentImage.endsWith('defaultteam.png') && (
          <img
            src={currentImage}
            className="h-full w-full object-cover"
            width={80}
            height={80}
            alt="Profile"
          />
        )}
        {/* <button
          type="button"
          className="absolute flex size-8 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white outline-none transition-[color,box-shadow] hover:bg-black/80 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          onClick={handleThumbnailClick}
          aria-label="Change profile picture"
        >
          <ImagePlusIcon size={16} aria-hidden="true" />
        </button> */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/*"
          aria-label="Upload profile picture"
        />
      </div>
      <div className="flex flex-col space-y-2">
        <div className="flex items-center gap-x-2">
          <Button size="sm" type="button" onClick={handleThumbnailClick}>
            Upload photo
          </Button>
          <Button
            size="sm"
            variant="outline"
            type="button"
            className="text-destructive hover:text-destructive"
            onClick={handleDelete}
            disabled={!canDelete}
          >
            Delete
          </Button>
        </div>
        {description && <FormDescription>{description}</FormDescription>}
      </div>
    </div>
  );
}

export const AvatarUploadInput = <T extends FieldValues>({
  control,
  name,
  label = 'Upload File',
  description,
  defaultImage,
  maxFileSize,
}: AvatarUploadInputProps<T>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Avatar
              defaultImage={defaultImage}
              onFileChange={field.onChange}
              description={description}
              maxFileSize={maxFileSize}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
